import React, { useState, useEffect, useRef } from 'react';

/**
 * Runs a callback function everytime a given delay has passed.
 *
 * @param {function} callback The function to run.
 * @param {number} delay The period to wait (in ms) between calls to the callback function.
 */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

/**
 * Returns the last given value once a given delay has passed from the last time the given value has been last updated.
 *
 * @param {object} value The value we want to have.
 * @param {number} delay The period to wait (in ms) before returning the given value.
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
